import { type RecurrentItem, type BalanceItem, Frequency } from "@/types.ts";
import dayjs, { Dayjs } from "dayjs";
import { format } from "numerable";

const endDate: Dayjs = dayjs().add(1, "y");
let currentDate: Dayjs;

interface Transaction {
  description: string;
  amount: number;
  due: number;
}


const transactions: Transaction[] = [];

/**
 * Given a RecurrentItem, this returns an array of Transactions that this item will generate
 * from the current date until the end of the range (endDate - set at the top of App.vue to 1 year)
 * @param {"I" | "E"} type
 * @param {RecurrentItem} item
 * @returns {Transaction[]}
 */
function getItemTX(type: "I" | "E", item: RecurrentItem): Transaction[] {
  let nextdue: Dayjs = dayjs.unix(item.nextdue);
  let delta = 0;
  let unit: "d" | "M" | "y" = "d";
  const itemTX: Transaction[] = [];

  switch (item.frequency) {
    case Frequency.Weekly:
      delta = 7;
      unit = "d";
      break;

    case Frequency.Fortnightly:
      delta = 14;
      unit = "d";
      break;

    case Frequency.Monthly:
      delta = 1;
      unit = "M";
      break;

    case Frequency.Sixmonthly:
      delta = 6;
      unit = "M";
      break;

    case Frequency.Yearly:
      delta = 1;
      unit = "y";
      break;

    /**
     * Assuming that the range for the forecast is 1 year, we can safely assume that
     * adding 2 years to the nextdue date will be enough to be outside the range
     */
    case Frequency.Once:
      delta = 2;
      unit = "y";
      break;

    default:
      throw new Error(`Invalid frequency: ${item.frequency}`);
  }

  while (!nextdue.isAfter(endDate)) {
    if (type === "I") {
      itemTX.push({
        description: item.description,
        amount: item.amount,
        due: nextdue.unix(),
      });
    } else {
      itemTX.push({
        description: item.description,
        amount: 0 - item.amount,
        due: nextdue.unix(),
      });
    }
    nextdue = nextdue.add(delta, unit);
  }

  return itemTX;
}

export function buildTransactions(
  startBalance: number,
  expenses: RecurrentItem[],
  income: RecurrentItem[],
): BalanceItem[] {
  console.log("Start building transactions");
  transactions.length = 0;
  currentDate = dayjs().startOf("day");

  // Process expenses and income
  expenses.forEach((item: RecurrentItem) => {
    transactions.push(...getItemTX("E", item));
  });
  income.forEach((item: RecurrentItem) => {
    transactions.push(...getItemTX("I", item));
  });
  console.log(`tx length: ${transactions.length}`);
  const sortedTX = [...transactions].sort((a, b) => a.due - b.due);

  const balance: BalanceItem[] = [];
  let currBalance = startBalance;
  for (
    let sx = 0;
    currentDate.isBefore(endDate);
    currentDate = currentDate.add(1, "d")
  ) {
    const description: string[] = [];
    if (sx < sortedTX.length) {
      while (sx < sortedTX.length && sortedTX[sx].due === currentDate.unix()) {
        currBalance += sortedTX[sx].amount;
        description.push(
          `${sortedTX[sx].description} (${format(
            Math.abs(sortedTX[sx].amount),
            "0,0.00",
            { currency: "USD" }
          )})`
        );
        sx++;
      }
    }
    balance.push({
      date: currentDate.unix(),
      description: description.join(", "),
      balance: currBalance,
    });
  }
  console.log("Done building transactions");
  return balance;
}
